import {
  useCallback,
  useEffect,
  useState,
} from 'react'
import type {
 AutonomyLevel
} from '../client'
import {
  automaticApprovalsService,
} from '../services/api'

// The autonomy level: how much the assistant does without asking. Three rungs
// (confirm / balanced / hands-off) over what used to be an on/off toggle for
// automatic approvals; "balanced" is that toggle's ON and the default since
// GET-12229, for everyone except accounts from the concluded
// `auto-approval-default-new-users` holdout arm. Hands-off is offered only
// when the backend says it is available for this user (the
// `hands-off-autonomy` flag); a user already on it still sees
// it selected when the flag goes off, so the card renders the level it was
// handed and never a guess.
//
// The card exists mainly as a way down. Before the Settings route the only
// way to stop the mode was to ask the assistant (`disable_automatic_approvals`),
// which was fine while it was opt-in and wrong the moment it became a default
// nobody chose. It renders whatever the answer is rather than hiding when the
// feature is off: "confirm" is a real, choosable state. It hides only while
// loading, and on a load error, where rendering controls would mean rendering
// a guess about what the assistant is doing.

const LEVELS: ReadonlyArray<{
  value: AutonomyLevel
  label: string
  detail: string
}> = [
  {
    value: 'confirm',
    label: 'Confirm',
    detail: 'Your assistant checks with you before every action.',
  },
  {
    value: 'balanced',
    label: 'Balanced',
    detail:
      'Goes ahead with small, low-risk things you’ve clearly asked for, and '
      + 'checks with you on anything it had to decide itself.',
  },
  {
    value: 'hands_off',
    label: 'Hands-off',
    detail:
      'Uses its own judgment on wording, timing and channel, and only comes to '
      + 'you for anything risky or unclear.',
  },
]

export function AutomaticApprovalsSection() {
  const [loaded, setLoaded] = useState(false)
  const [failed, setFailed] = useState(false)
  const [level, setLevel] = useState<AutonomyLevel>('confirm')
  const [handsOffAvailable, setHandsOffAvailable] = useState(false)
  const [lockedOn, setLockedOn] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      const status = await automaticApprovalsService.getStatus()
      setLevel(status.level)
      setHandsOffAvailable(status.hands_off_available)
      setLockedOn(status.locked_on)
    } catch {
      setFailed(true)
    } finally {
      setLoaded(true)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const choose = async (next: AutonomyLevel) => {
    if (busy || next === level) return
    // A locked-on account cannot go down to "confirm"; the other moves are
    // still theirs to make.
    if (lockedOn && next === 'confirm') return
    setBusy(true)
    setError(null)
    try {
      // The response is the truth, not `next`: a move to "confirm" that a
      // central release group overrides comes back "balanced", and the control
      // has to settle there rather than show a state the backend didn't agree to.
      const status = await automaticApprovalsService.setLevel(next)
      setLevel(status.level)
      setHandsOffAvailable(status.hands_off_available)
      setLockedOn(status.locked_on)
      if (status.level !== next) {
        setError(
          'Your account is set to Balanced by Catch, so Confirm isn’t available '
          + 'here. Message your assistant and we’ll sort it.',
        )
      }
    } catch {
      setError('Couldn’t change that just now. Try again in a moment.')
    } finally {
      setBusy(false)
    }
  }

  if (!loaded || failed) return null

  const options = LEVELS.filter(
    (option) => option.value !== 'hands_off' || handsOffAvailable || level === 'hands_off',
  )
  const current = LEVELS.find((option) => option.value === level)

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-base font-semibold text-gray-900">Assistant autonomy</h3>
      <p className="mt-1 text-sm text-gray-600">
        How much your assistant does on its own. Whatever you pick, anything
        risky &mdash; an unfamiliar recipient, money, deleting something, or
        sharing your details &mdash; still comes to you first.
      </p>

      <fieldset className="mt-4 grid gap-2 sm:grid-cols-3" disabled={busy}>
        <legend className="sr-only">Autonomy level</legend>
        {options.map((option) => {
          const selected = option.value === level
          const disabled = busy || (lockedOn && option.value === 'confirm')
          return (
            <label
              key={option.value}
              className={`cursor-pointer rounded-lg border px-3 py-2 text-sm transition-colors focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 ${
                selected
                  ? 'border-primary-600 bg-primary-50 text-primary-900'
                  : 'border-gray-200 text-gray-700 hover:border-gray-300'
              } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            >
              <input
                type="radio"
                name="autonomy-level"
                value={option.value}
                checked={selected}
                disabled={disabled}
                onChange={() => void choose(option.value)}
                className="sr-only"
              />
              <span className="block font-medium">{option.label}</span>
            </label>
          )
        })}
      </fieldset>

      {current && <p className="mt-3 text-xs text-gray-500">{current.detail}</p>}

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      {lockedOn && !error && (
        <p className="mt-3 text-xs text-gray-500">
          Set to Balanced for your account by Catch.
        </p>
      )}
    </div>
  )
}
